import { useCallback } from 'react';
import * as Sentry from '@sentry/react';
import {
  RecordingControlStatus,
  useCreateOneCallRecordingMutation,
  useUpdateOneCallRecordingMutation,
} from 'shared/types/graphql';
import { PhoneCallRecordingEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

interface UsePhoneCallRecordingProps {
  recordingId: string | null;
  setRecordingId: (recordingId: string) => void;
}

export const useTwilioPhoneCallRecording = ({
  recordingId,
  setRecordingId,
}: UsePhoneCallRecordingProps): {
  startRecording: (callSid: string) => void;
  pauseRecording: () => void;
} => {
  const [createOneCallRecording] = useCreateOneCallRecordingMutation();
  const [updateOneCallRecording] = useUpdateOneCallRecordingMutation();
  const { trackEvent } = useTracking();

  const startRecording = useCallback(
    async (callSid: string) => {
      let newCallRecordingId;
      if (recordingId) {
        // Resume existing recording
        updateOneCallRecording({
          variables: {
            where: {
              id: recordingId,
            },
            data: {
              status: RecordingControlStatus.InProgress,
            },
          },
        }).catch((e) => {
          Sentry.captureException(e);
          console.warn(e);
        });
        trackEvent(PhoneCallRecordingEvents.RecordingResumed, { recordingId });
      } else {
        // Create new recording
        try {
          const { data: callRecordingData } = await createOneCallRecording({
            variables: {
              callSid,
            },
          });
          newCallRecordingId = callRecordingData?.createOneCallRecording?.id;
        } catch (e) {
          Sentry.captureException(e);
          console.warn(e);
        }

        if (newCallRecordingId) {
          setRecordingId(newCallRecordingId);
        }

        trackEvent(PhoneCallRecordingEvents.RecordingStarted, {
          recordingId: recordingId || newCallRecordingId,
        });
      }
    },
    [recordingId, updateOneCallRecording, trackEvent, createOneCallRecording, setRecordingId],
  );

  const pauseRecording = useCallback(async () => {
    if (!recordingId) {
      console.error('No recordingId to pause');
      return;
    }
    updateOneCallRecording({
      variables: {
        where: {
          id: recordingId,
        },
        data: {
          status: RecordingControlStatus.Paused,
        },
      },
    }).catch((e) => {
      Sentry.captureException(e);
      console.warn(e);
    });

    trackEvent(PhoneCallRecordingEvents.RecordingPaused, { recordingId });
  }, [recordingId, updateOneCallRecording, trackEvent]);

  return { startRecording, pauseRecording };
};
